<template>
  <template v-if="options.length > 0">
    <!--센드박스 설정(내부용)-->
    <template v-if="isTester">
      <p class="smaller accent setting-title">샌드박스 설정</p>
      <div class="setting-container">
        <!--실험실 컨트롤러-->
        <ToggleSecretLanguageChange />
        <!--마이크 컨트롤러-->
        <ToggleSecretMicController />
      </div>
    </template>

    <!-- 일반설정 -->
    <template v-if="isGeneralSettingEnabled">
      <p class="smaller accent setting-title">
        {{ t('setting') }}
      </p>
      <div class="setting-container">
        <!-- 통역 기록 다운로드 -->
        <ButtonDownloadHistory v-if="isHistoryDownloadEnabled" />
        <!--다크모드-->
        <ToggleDarkMode v-if="isDarkModeEnabled" />
        <!--전문가용 멀티 채널 지원 (MCSP)-->
        <ToggleMCSController v-if="isMCSPEnabled" />
        <!--멀티 채널 지원 (MCS)-->
        <toggleSMCSController v-if="isMCSEnabled" />
        <!-- 보조화면 원격 제어 -->
        <ToggleAuxiliaryDisplayControl v-if="isAuxiliaryDisplayControlEnabled" />
        <!--모든참가자마이크음소거-->
        <ToggleAllowMic v-if="isMuteGuestsEnabled" />
        <!--등록된나의목소리만인식-->
        <ToggleMyVoice v-if="isTTSEnabled" />
        <!--나의언어만보기-->
        <ToggleMyLanguage v-if="isMyLanguageEnabled" />
        <!--라이브음성재생-->
        <ToggleFastInterpretation v-if="isFastInterpretationEnabled" />
        <!--마주보기-->
        <SingleToggleFaceToFace v-if="isFaceToFaceEnabled" />
      </div>
      <div class="divider" />
    </template>
  </template>

  <!--텍스트사이즈컨트롤-->
  <template v-if="isTextSizeControlEnabled">
    <GaugeTextSizeMultiplier :is-global="true" />
    <div class="divider" />
  </template>
  <!--확정조절-->
  <template v-if="isInterpretationSpeedControlEnabled">
    <ControlInterpretationSpeed />
    <div class="divider" />
  </template>
  <!--음성구분조절-->
  <template v-if="isVoiceControlEnabled">
    <ControlVoiceSize />
    <div class="divider" />
  </template>

  <template v-if="isMobile">
    <p class="smaller accent setting-title last">
      {{ t('default_settings') }}
    </p>
    <button v-if="isUserGuideEnabled" @click="redirectToManualPage(lang, chatType)" class="guide-btn single-guide">
      <IconGuide class="icon" />
      <span>{{ t('label_view_guide') }}</span>
    </button>
    <div class="setting-container last">
      <button v-if="isAttendeeEnabled" class="general-button-wrapper" @click="toggleAttendeesModal">
        <IconUser class="icon-medium" />
        <p class="small">{{ t('view_participant_list') }}</p>
      </button>
      <button class="general-button-wrapper" @click="confirmHangUp">
        <IconExit class="icon-medium" />
        <p class="small">{{ t('end_of_call_button') }}</p>
      </button>
    </div>
  </template>

  <div v-if="showAttendeesModal" class="attendees-modal-overlay" @click="toggleAttendeesModal">
    <ModalSmall :title="t('participants')" class="attendees-modal">
      <DropdownAttendees
        :attendees="attendees"
        :partner-person="callStore.qrCodeInfo.accessType === 'EMPLOYEE'"
        @evict="alertConfirmEvictGuest"
      />
    </ModalSmall>
  </div>
  <div class="scon-logo">
    <img :src="getAssetImageUrl('common/logos/logo-sconai-full-timer.png')" alt="" />
  </div>
</template>

<script setup lang="ts">
/**
 * @description 통역 > 설정 > 노출 조건 정리
 * - 관련 기획: {@link https://sconai.atlassian.net/wiki/spaces/SconAI/pages/718274561}
 * - 관련 상수: {@link CHAT_SETTING_OPTIONS}
 */
import { useI18n } from 'vue-i18n';

import { getAssetImageUrl } from '@/utils/getAssetImageUrl';
import ToggleAllowMic from '@/components/domains/chat/chat-general/ToggleAllowMic.vue';
import ToggleAuxiliaryDisplayControl from '@/components/domains/chat/chat-general/ToggleAuxiliaryDisplayControl.vue';
import ToggleDarkMode from '@/components/domains/chat/chat-general/ToggleDarkMode.vue';
import ToggleFastInterpretation from '@/components/domains/chat/chat-general/ToggleFastInterpretation.vue';
import ToggleMCSController from '@/components/domains/chat/chat-general/ToggleMCSController.vue';
import ToggleMyLanguage from '@/components/domains/chat/chat-general/ToggleMyLanguage.vue';
import ToggleMyVoice from '@/components/domains/chat/chat-general/ToggleMyVoice.vue';
import ToggleSecretLanguageChange from '@/components/domains/chat/chat-general/ToggleSecretLanguageChange.vue';
import ToggleSecretMicController from '@/components/domains/chat/chat-general/ToggleSecretMicController.vue';
import ToggleSMCSController from '@/components/domains/chat/chat-general/ToggleSMCSController.vue';
import SingleToggleFaceToFace from '@/components/domains/chat/chat-single/SingleToggleFaceToFace.vue';
import ButtonDownloadHistory from '@/components/domains/chat/common/ButtonDownloadHistory.vue';
import ControlInterpretationSpeed from '@/components/domains/chat/common/ControlInterpretationSpeed.vue';
import ControlVoiceSize from '@/components/domains/chat/common/ControlVoiceSize.vue';
import DropdownAttendees from '@/components/domains/chat/common/DropdownAttendees.vue';
import GaugeTextSizeMultiplier from '@/components/domains/chat/common/GaugeTextSizeMultiplier.vue';
import IconExit from '@/components/icons/IconExit.vue';
import IconGuide from '@/components/icons/IconGuide.vue';
import IconUser from '@/components/icons/IconUser.vue';
import ModalSmall from '@/components/shared/composite/ModalSmall.vue';
import { useButtonToggleMic } from '@/composables/domains/chat/useButtonToggleMic';
import { useHangUpCommon } from '@/composables/domains/chat/useHangupCommon';
import { useDeviceSize } from '@/composables/shared/useDeviceSize';
import { useNotification } from '@/composables/shared/useNotification';
import { useCallStore } from '@/stores/call';
import { useMessageStore } from '@/stores/message';
import { useMicrophoneStore } from '@/stores/microphone';
import { UserType, useUserStore } from '@/stores/user';
import { Nullable, TypeCall, TypeDomain } from '@/types/common';
import { ChatSettingOptions } from '@/types/components';
import { Attendee } from '@/types/socket';
import { DOMAIN } from '@/utils/constants/common';
import { ij_isVisibleButtonMyLanguage, ij_setBottomBarStatus } from '@/utils/injectionKeys';
import redirectToManualPage from '@/utils/redirectToManualPage';

const props = withDefaults(
  defineProps<{
    options: ChatSettingOptions[];
    targetEl?: Nullable<HTMLElement>;
  }>(),
  {
    options: () => [],
  }
);

const emit = defineEmits<{ toggleSettingModal: []; toggleScreenModal: [] }>();

const setBottomBarStatus = inject(ij_setBottomBarStatus, () => {});
const isVisibleButtonMyLanguage = inject(ij_isVisibleButtonMyLanguage) as Ref<boolean>;

const { t } = useI18n();

const microphoneStore = useMicrophoneStore();
const userStore = useUserStore();
const callStore = useCallStore();
const messageStore = useMessageStore();
const route = useRoute();

const { alertConfirmEvictGuest } = useNotification();
const { isMobile } = useDeviceSize();
const { isVisibleMic } = useButtonToggleMic();
const { confirmHangUp } = useHangUpCommon({
  setBottomBarStatus,
  emitClose: () => emit('toggleSettingModal'),
});

const showAttendeesModal = ref<boolean>(false);

const lang = computed<'ko' | 'en'>(() => {
  return userStore.pageLanguage === 'ko' ? 'ko' : 'en';
});

const chatType = computed<TypeCall>(() => {
  return route.meta.chatType;
});

const attendees = computed<Attendee[]>(() => {
  return messageStore.activeAttendees;
});

const userType = computed<boolean>(() => {
  const { userType = '' } = userStore.userInfo || {};
  return userType;
});

const isTester = computed<boolean>(() => {
  return userStore.userInfo.isTester;
});

const domain = computed<TypeDomain>(() => {
  const metaDomain = route.meta.domain;
  return metaDomain && Object.values(DOMAIN).includes(metaDomain) ? metaDomain : 'CHAT';
});

const isGeneralSettingEnabled = computed<boolean>(() => {
  return (
    isHistoryDownloadEnabled.value ||
    isDarkModeEnabled.value ||
    isMCSPEnabled.value ||
    isMCSEnabled.value ||
    isAuxiliaryDisplayControlEnabled.value ||
    isMuteGuestsEnabled.value ||
    isTTSEnabled.value ||
    isMyLanguageEnabled.value ||
    isFastInterpretationEnabled.value ||
    isFaceToFaceEnabled.value
  );
});

const isHistoryDownloadEnabled = computed<boolean>(() => {
  const { storeAgreement = false } = userStore.userInfo;
  return props.options.includes('DOWNLOAD_HISTORY') && storeAgreement && UserType.isPartner(userType.value);
});

const isDarkModeEnabled = computed<boolean>(() => {
  return props.options.includes('DARK_MODE');
});

const isMCSPEnabled = computed<boolean>(() => {
  return UserType.isPartner(userType.value) && callStore.qrOptions.MCSP;
});

const isMCSEnabled = computed<boolean>(() => {
  return UserType.isPartner(userType.value) && callStore.qrOptions.MCS;
});

const isAuxiliaryDisplayControlEnabled = computed<boolean>(() => {
  return (
    props.options.includes('AUXILIARY_DISPLAY_CONTROL') &&
    (callStore.qrOptions.screenMode || callStore.qrOptions.subtitleMode) &&
    UserType.isPartner(userType.value) &&
    domain.value !== 'SELF_SERVICE'
  );
});

const isMuteGuestsEnabled = computed<boolean>(() => {
  return props.options.includes('MUTE_GUEST') && UserType.isPartner(userType.value) && callStore.qrOptions.showGuestMic;
});

const isTTSEnabled = computed<boolean>(() => {
  const hasVoiceId = microphoneStore.microphonesWithVoice.some(({ voiceId }) => voiceId);
  return props.options.includes('TTS') && !!isVisibleMic.value && hasVoiceId;
});

const isMyLanguageEnabled = computed<boolean>(() => {
  return props.options.includes('ONLY_MY_LANGUAGE') && isVisibleButtonMyLanguage.value;
});

const isFastInterpretationEnabled = computed<boolean>(() => {
  return props.options.includes('FAST_INTERPRETATION') && UserType.isPartner(userType.value);
});

const isFaceToFaceEnabled = computed<boolean>(() => {
  return props.options.includes('FACE_TO_FACE');
});

const isTextSizeControlEnabled = computed<boolean>(() => {
  return props.options.includes('TEXT_SIZE_CONTROL');
});

const isInterpretationSpeedControlEnabled = computed<boolean>(() => {
  return props.options.includes('INTERPRETATION_SPEED_CONTROL') && UserType.isPartner(userType.value);
});

const isVoiceControlEnabled = computed<boolean>(() => {
  return props.options.includes('VOICE_CONTROL');
});

const isUserGuideEnabled = computed<boolean>(() => {
  return props.options.includes('USER_GUIDE') && domain.value === 'SELF_SERVICE';
});

const isAttendeeEnabled = computed<boolean>(() => {
  return props.options.includes('ATTENDEE_LIST') && callStore.qrOptions.showAttendee;
});

// 이 드랍다운의 위치 조정
const setPositionSettingMenu = () => {
  const myLanguage = document.querySelector('.my-language');
  const container = document.querySelector('.setting-bg-container') as HTMLElement;
  if (container && myLanguage) {
    const rect = myLanguage.getBoundingClientRect();
    const addPx = window.innerWidth < 768 ? 5 : -3;

    // bottom 값을 top으로 변환
    const bottomValue = container.clientHeight * -1 - addPx;
    const leftValue = rect.left - rect.width;

    // .abc 요소에 값 설정
    container.style.top = `${bottomValue + 10}px`;
    container.style.left = `${leftValue + 10}px`;
  }
};

// 참석자 리스트 모달 여/닫기
const toggleAttendeesModal = () => {
  showAttendeesModal.value = !showAttendeesModal.value;
};

onMounted(() => {
  setPositionSettingMenu();
});
</script>

<style scoped lang="scss">
.general-button-wrapper {
  width: 100%;
  display: flex;
  justify-content: left;
  align-items: center;
  text-align: left;
  border-radius: 8px;
  padding: 0.75rem;
  gap: 0.75rem;
  color: var(--font-default);
  fill: var(--font-dim-dark);
  overflow: hidden;
  z-index: 0;
  flex-shrink: 0;
  transition:
    border-radius 0.5s ease,
    background-color 0.5s ease;
}

.general-button-wrapper:hover {
  background-color: var(--surface-hover-gray-dim);
}

.setting-container {
  position: relative;
  width: 100%;
  margin-top: 0.25rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  border-radius: 8px;
}

.setting-container .divider:last-child {
  display: none;
}

.setting-title {
  margin-bottom: 0.25rem;
}

.last {
  margin-top: 0.5rem;
}

.divider {
  margin-top: 0.75rem;
  margin-bottom: 0.75rem;
}
.divider:last-of-type {
  display: none;
}

.guide-btn {
  margin-top: 1rem;
  padding: 0.75rem;
  display: flex;
  border-radius: 0.5rem;
  gap: 0.75rem;
  width: 100%;
  justify-content: flex-start;
  text-decoration: none;
  align-items: center;

  svg {
    width: 22px;
    height: 22px;
    object-fit: contain;
    color: $color-gray-700;
  }
  span {
    color: var(--font-default);
    font-size: $font-size-psmall;
    font-weight: 400;
  }
}

.attendees-modal-overlay {
  position: fixed;
  width: 100%;
  height: 100dvh;
  left: 0;
  top: 0;

  .attendees-modal {
    position: absolute;
  }
}

.scon-logo {
  width: 100%;
  height: 2rem;
  background: #fbfbfb;
  border-radius: $size-s;
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    width: 65px;
    object-fit: contain;
  }
}
</style>
